// Access token: memory-only (short-lived, 15m).
// Refresh token: httpOnly cookie managed by the backend — never touches JS.
//
// Multi-tab strategy (Web Locks API, all modern browsers):
//   Tabs serialize /auth/refresh calls via a named lock ('andhub_refresh').
//   Inside the lock, fn checks getAccessToken() — if set by another tab's
//   broadcast, it skips the network call.
//   Fallback: localStorage timestamp lock for browsers without Web Locks.

const LOCK_KEY = 'andhub_refresh_lock';
const LOCK_TTL = 15_000; // ms — fallback only

let accessToken: string | null = null;

const channel = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('andhub_token')
  : null;

type BroadcastMsg =
  | { type: 'TOKEN_REFRESHED'; token: string }
  | { type: 'REFRESH_FAILED' }
  | { type: 'REFRESH_TRANSIENT_FAIL' }
  | { type: 'LOGGED_OUT' };

channel?.addEventListener('message', (e: MessageEvent<BroadcastMsg>) => {
  if (e.data.type === 'TOKEN_REFRESHED') {
    accessToken = e.data.token;
  } else if (e.data.type === 'LOGGED_OUT') {
    accessToken = null;
    if (!window.location.pathname.startsWith('/login')) {
      try { sessionStorage.removeItem('andhub_user'); } catch { /* ignore */ }
      try { localStorage.removeItem('andhub_logged_in'); } catch { /* ignore */ }
      window.location.href = '/login';
    }
  }
});

export const tokenStore = {
  getAccessToken: (): string | null => accessToken,

  setAccessToken: (token: string): void => {
    accessToken = token;
    channel?.postMessage({ type: 'TOKEN_REFRESHED', token } satisfies BroadcastMsg);
  },

  /**
   * Silently clear the in-memory token.
   * Use when the token has expired and a silent refresh is about to happen.
   * Does NOT broadcast — other tabs keep their own valid tokens.
   */
  invalidateAccessToken: (): void => {
    accessToken = null;
  },

  /**
   * Clear the token and tell all other tabs to log out.
   * Use only for explicit logout or unrecoverable auth failure (e.g. refresh 401).
   */
  clearAccessToken: (): void => {
    accessToken = null;
    channel?.postMessage({ type: 'LOGGED_OUT' } satisfies BroadcastMsg);
  },

  broadcastRefreshFailed: (): void => {
    channel?.postMessage({ type: 'REFRESH_FAILED' } satisfies BroadcastMsg);
  },

  broadcastRefreshTransientFail: (): void => {
    channel?.postMessage({ type: 'REFRESH_TRANSIENT_FAIL' } satisfies BroadcastMsg);
  },

  /**
   * Run `fn` exclusively — only one tab at a time may call /auth/refresh.
   *
   * fn should call tokenStore.getAccessToken() at the start and skip if
   * already set — this handles the case where another tab refreshed and
   * broadcast the token while this tab was queued for the lock.
   *
   * Web Locks path: truly atomic cross-tab mutex (all modern browsers).
   * Fallback: localStorage timestamp + BroadcastChannel for older browsers.
   */
  runWithRefreshLock: (fn: () => Promise<void>): Promise<void> => {
    if (typeof navigator !== 'undefined' && 'locks' in navigator) {
      type LockManager = { request: (name: string, cb: () => Promise<void>) => Promise<void> };
      return (navigator as unknown as { locks: LockManager }).locks.request(
        'andhub_refresh',
        () => fn(),
      );
    }

    // localStorage fallback
    const existing = localStorage.getItem(LOCK_KEY);
    if (existing && Date.now() - parseInt(existing, 10) < LOCK_TTL) {
      // Another tab holds the lock — wait for its result broadcast
      return new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => { cleanup(); resolve(); }, LOCK_TTL);

        const handler = (e: MessageEvent<BroadcastMsg>) => {
          cleanup();
          if (e.data.type === 'TOKEN_REFRESHED') {
            resolve();
          } else if (e.data.type === 'REFRESH_FAILED' || e.data.type === 'LOGGED_OUT') {
            reject(Object.assign(new Error('token_expired'), { isTokenExpired: true }));
          } else if (e.data.type === 'REFRESH_TRANSIENT_FAIL') {
            reject(new Error('transient_error'));
          }
        };

        function cleanup() {
          clearTimeout(timer);
          channel?.removeEventListener('message', handler);
        }

        channel?.addEventListener('message', handler);
      });
    }

    localStorage.setItem(LOCK_KEY, String(Date.now()));
    return fn().finally(() => {
      try { localStorage.removeItem(LOCK_KEY); } catch { /* ignore */ }
    });
  },
};
